import React, { useState } from "react";
import Logger from "../../../services/Logger";
import {
    Alert,
    Button,
    Card,
    CardContent,
    CircularProgress,
    Grid,
    TextField,
    Typography
} from "@mui/material";
import Item from "../../components/Item";
import IngestService from "./IngestService";
import ConfirmDialog from "../../components/ConfirmDialog";

const AgentWikinews = () => {
    const [wikinewsArticleTitle, setWikinewsArticleTitle] = useState("");
    const [wikinewsProcessingAll, setWikinewsProcessingAll] = useState(false);
    const [wikinewsProcessingArticle, setWikinewsProcessingArticle] = useState(false);
    const [wikinewsError, setWikinewsError] = useState(null);
    const [wikinewsSuccess, setWikinewsSuccess] = useState(null);
    const [showWikinewsAllConfirmDialog, setShowWikinewsAllConfirmDialog] = useState(false);
    const [showWikinewsArticleConfirmDialog, setShowWikinewsArticleConfirmDialog] = useState(false);

    const handleWikinewsProcessAll = () => {
        setShowWikinewsAllConfirmDialog(true);
    };

    const confirmWikinewsProcessAll = async () => {
        try {
            setWikinewsProcessingAll(true);
            setWikinewsError(null);
            setWikinewsSuccess(null);

            const result = await IngestService.processAllWikinewsArticles();
            
            if (result.success) {
                setWikinewsSuccess("All Wikinews articles have been processed successfully");
            } else {
                setWikinewsError(result.error);
            }
        } catch (err) {
            Logger.error("Error processing all Wikinews articles:", err);
            setWikinewsError(err.message || "Failed to process Wikinews articles");
        } finally {
            setWikinewsProcessingAll(false);
        }
    };

    const handleWikinewsProcessArticle = () => {
        if (!wikinewsArticleTitle || !wikinewsArticleTitle.trim()) {
            setWikinewsError("Please enter an article title");
            return;
        }

        setShowWikinewsArticleConfirmDialog(true);
    };

    const confirmWikinewsProcessArticle = async () => {
        try {
            setWikinewsProcessingArticle(true);
            setWikinewsError(null);
            setWikinewsSuccess(null);

            const result = await IngestService.processWikinewsArticle(wikinewsArticleTitle.trim());
            
            if (result.success) {
                setWikinewsSuccess(`Wikinews article "${wikinewsArticleTitle}" has been processed successfully`);
                setWikinewsArticleTitle(""); // Clear the input after success
            } else {
                setWikinewsError(result.error);
            }
        } catch (err) {
            Logger.error("Error processing Wikinews article:", err);
            setWikinewsError(err.message || "Failed to process Wikinews article");
        } finally {
            setWikinewsProcessingArticle(false);
        }
    };

    return (
        <Item sx={{ height: '100%', width: '100%' }}>
            <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                <CardContent sx={{ flexGrow: 1 }}>
                    <Typography variant="h5" sx={{ mb: 3, fontWeight: 600 }}>
                        Wikinews
                    </Typography>
                    <Grid container spacing={2} sx={{ mb: 3 }}>
                        <Grid size={{ xs: 12 }}>
                            <Grid container spacing={2} alignItems="center" justifyContent="space-between">
                                <Grid size="auto">
                                    <Typography variant="body1" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', height: '100%' }}>
                                         All Articles:
                                    </Typography>
                                </Grid>
                                <Grid size="auto">
                                    <Button
                                        variant="contained"
                                        color="primary"
                                        onClick={handleWikinewsProcessAll}
                                        disabled={true}
                                        sx={{ minWidth: 140 }}
                                    >
                                        {wikinewsProcessingAll ? (
                                            <>
                                                <CircularProgress size={20} sx={{ mr: 1 }} />
                                                Processing...
                                            </>
                                        ) : (
                                            "Process All"
                                        )}
                                    </Button>
                                </Grid>
                            </Grid>
                        </Grid>
                        <Grid size={{ xs: 12 }}>
                            <Grid container spacing={2} alignItems="center" justifyContent="space-between">
                                <Grid size="auto">
                                    <Typography variant="body1" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', height: '100%', whiteSpace: 'nowrap' }}>
                                         Specific Article:
                                    </Typography>
                                </Grid>
                                <Grid size={true}>
                                    <TextField
                                        label="Article Title"
                                        value={wikinewsArticleTitle}
                                        onChange={(e) => setWikinewsArticleTitle(e.target.value)}
                                        fullWidth
                                        size="small"
                                        disabled={wikinewsProcessingAll || wikinewsProcessingArticle}
                                        placeholder="Enter article title"
                                    />
                                </Grid>
                                <Grid size="auto">
                                    <Button
                                        variant="contained"
                                        color="primary"
                                        onClick={handleWikinewsProcessArticle}
                                        disabled={true}
                                        sx={{ minWidth: 140 }}
                                    >
                                        {wikinewsProcessingArticle ? (
                                            <>
                                                <CircularProgress size={20} sx={{ mr: 1 }} />
                                                Processing...
                                            </>
                                        ) : (
                                            "Execute"
                                        )}
                                    </Button>
                                </Grid>
                            </Grid>
                        </Grid>
                    </Grid>
                    {wikinewsSuccess && (
                        <Alert severity="success" sx={{ mb: 2 }}>
                            {wikinewsSuccess}
                        </Alert>
                    )}
                    {wikinewsError && (
                        <Alert severity="error" sx={{ mb: 2 }}>
                            {wikinewsError}
                        </Alert>
                    )}
                    <Alert severity="info">
                        Process Wikinews articles to create puzzles. You can process all articles or a specific article by title.
                    </Alert>
                </CardContent>
            </Card>

            <ConfirmDialog
                open={showWikinewsAllConfirmDialog}
                setOpen={setShowWikinewsAllConfirmDialog}
                title="Confirm Process All Wikinews Articles"
                message="Are you sure you want to process all Wikinews articles? This may take a while."
                callback={confirmWikinewsProcessAll}
            />

            <ConfirmDialog
                open={showWikinewsArticleConfirmDialog}
                setOpen={setShowWikinewsArticleConfirmDialog}
                title="Confirm Process Wikinews Article"
                message={`Are you sure you want to process the Wikinews article "${wikinewsArticleTitle}"?`}
                callback={confirmWikinewsProcessArticle}
            />
        </Item>
    );
};

export default AgentWikinews;
